import React, { useState } from 'react';
import { createProduct, updateProduct } from '@/services/api';

const ProductForm = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState(product || {
    name: '',
    description: '',
    product_type: 'red_wine',
    region: '',
    country: 'France',
    price: 0,
    image_url: '',
    stock: 0,
    volume: '750ml',
    alcohol_content: '13%'
  });

  // Category structure
  const categories = {
    'France': ['Bordeaux', 'Loire Valley', 'Burgundy', 'Rhones', 'Provence', 'Languedoc', 'Champagne'],
    'Italy': ['Piedmont', 'Veneto', 'Trentino-Alto Adige', 'Friuli-Venezia Giulia', 'Emilia-Romagna', 'Tuscany', 'Abruzzo', 'Puglia'],
    'Spirits': ['Vodka', 'Gin', 'Tequila', 'Mezcal', 'Whisky', 'Bourbon']
  };

  const handleCountryChange = (country) => {
    setFormData({ 
      ...formData, 
      country: country,
      region: '',
      product_type: country === 'Spirits' ? 'spirits' : formData.product_type
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (product) {
        await updateProduct(product.id, formData);
      } else {
        await createProduct(formData);
      }
      onSave();
      onClose();
    } catch (error) {
      alert('Failed to save product: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{product ? 'Edit Product' : 'Add New Product'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Selection */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={formData.country}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                required
              >
                <option value="">Select Category</option>
                <option value="France">France</option>
                <option value="Italy">Italy</option>
                <option value="Spirits">Spirits</option>
              </select>
            </div>

            {formData.country && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region/Subcategory *</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  required
                >
                  <option value="">Select Region</option>
                  {categories[formData.country]?.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="e.g., Château Margaux 2015"
            />
          </div>

          {/* Wine Type - Only show if not Spirits */}
          {formData.country !== 'Spirits' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wine Type *</label>
              <select
                value={formData.product_type}
                onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                <option value="red_wine">Red Wine</option>
                <option value="white_wine">White Wine</option>
                <option value="rose_wine">Rosé Wine</option>
                <option value="sparkling_wine">Sparkling Wine</option>
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="Product description..."
            />
          </div>

          {/* Price and Stock */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock *</label>
              <input
                type="number"
                required
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Volume</label>
              <input
                type="text"
                value={formData.volume}
                onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="750ml"
              />
            </div>
          </div>

          {/* Alcohol Content and Image */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alcohol Content</label>
              <input
                type="text"
                value={formData.alcohol_content}
                onChange={(e) => setFormData({ ...formData, alcohol_content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="13%"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition"
              data-testid="save-product-btn"
            >
              Save Product
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-900 rounded-lg font-semibold hover:bg-gray-300 transition"
              data-testid="cancel-product-btn"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
